<template>
	<v-dialog
		:model-value="modelValue"
		max-width="720"
		scrollable
		@update:model-value="$emit('update:modelValue', $event)"
	>
		<v-card>
			<v-card-title class="text-wrap">
				This paper already has extractions
			</v-card-title>
			<v-card-text>
				<p class="mb-3">
					<strong>{{ paperName }}</strong> is already in Dactyl with these
					extractions:
				</p>
				<ul class="attach-extraction-list pl-5 mb-3">
					<li
						v-for="line in extractionLines"
						:key="line.uuid"
						class="text-body-2"
					>
						{{ line.text }}
					</li>
				</ul>
				<p class="mb-3">
					Keep them to use this work in
					<strong>{{ projectName }}</strong
					>, or restart extraction to extract the paper again from scratch.
				</p>
				<v-alert
					v-if="preview.blockedByQc"
					type="warning"
					variant="tonal"
					density="compact"
				>
					This paper has a QC record, so its extraction can't be restarted. You
					can still attach it with its current extractions.
				</v-alert>
				<div v-else class="d-flex align-start ga-2 text-body-2">
					<v-icon color="warning" size="small" class="flex-shrink-0">
						mdi-alert
					</v-icon>
					<span>
						Note: "Attach and restart extractions" also removes the extractions
						from all other projects.
					</span>
				</div>
			</v-card-text>
			<v-card-actions class="ga-2">
				<v-spacer />
				<v-btn
					variant="text"
					:disabled="submitting"
					@click="$emit('update:modelValue', false)"
				>
					Cancel
				</v-btn>
				<v-btn
					color="error"
					variant="outlined"
					:disabled="submitting || preview.blockedByQc"
					:loading="submitting === 'restart'"
					@click="$emit('restart')"
				>
					Attach and restart extractions
				</v-btn>
				<v-btn
					color="primary"
					variant="flat"
					:disabled="submitting"
					:loading="submitting === 'keep'"
					@click="$emit('keep')"
				>
					Attach with extractions
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script>
// DCTL-XXXX (DHELP-437): the add-paper dialog's "Attach to this project" on a
// paper Dactyl already holds WITH extractions. A paper is one record shared by
// every project it is in, so attaching brings its extractions (and the board
// stage they produce) along. This asks first: keep them, or restart
// extraction from scratch (POST /papers/:uuid/attach-project with
// restartExtraction). `preview` is GET on the same path - the exact
// extractions a restart would retire, and whether a QC record refuses it.

// Same mapping the read-only viewer routing and the manager board use.
const EXTRACTION_FORMAT_LABELS = {
	WPD: "SDEv1",
	DACTYL_SDE: "SDEv2",
};

const STATE_LABELS = {
	completed: "completed",
	reopened: "reopened",
	in_progress: "in progress",
};

export default {
	name: "AttachExistingPaperDialog",

	props: {
		modelValue: { type: Boolean, default: false },
		paperName: { type: String, default: "This paper" },
		projectName: { type: String, default: "this project" },
		preview: {
			type: Object,
			required: true,
		},
		// false, or which action is in flight ("keep" / "restart").
		submitting: { type: [Boolean, String], default: false },
	},

	emits: ["update:modelValue", "keep", "restart"],

	computed: {
		// "Natasha Nayak - SDEv1, completed, last saved Jul 22, 2025"
		extractionLines() {
			return (this.preview.extractions || []).map((extraction) => {
				const user = extraction.user;
				const name = user
					? `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
						user.email ||
						"Unknown user"
					: "Unknown user";
				const parts = [
					EXTRACTION_FORMAT_LABELS[extraction.dataFormat],
					STATE_LABELS[extraction.state],
				].filter(Boolean);
				const savedOn = this.formatDate(extraction.updatedAt);
				if (savedOn) parts.push(`last saved ${savedOn}`);
				return {
					uuid: extraction.uuid,
					text: parts.length ? `${name} - ${parts.join(", ")}` : name,
				};
			});
		},
	},

	methods: {
		formatDate(iso) {
			if (!iso) return null;
			const date = new Date(iso);
			if (isNaN(date.getTime())) return null;
			return date.toLocaleDateString(undefined, {
				month: "short",
				day: "numeric",
				year: "numeric",
			});
		},
	},
};
</script>

<style scoped lang="scss">
// A paper can carry many live extractions (dev papers hold 20+ from e2e runs);
// the list scrolls on its own so the actions stay in view.
.attach-extraction-list {
	max-height: 220px;
	overflow-y: auto;
}
</style>
